import React, { useEffect, useRef, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'material-react-toastify';

import createPdf from './pdf';
import PrintPhotoBrowser from './PrintPhotoBrowser';
import ImageElement from './ImageElement';
import TextElement from './TextElement';
import ToolMenu from './ToolMenu';
import Layers from './Layers';
import TextForm from './TextForm';
import PrintSaveForm from './PrintSaveForm';
import Dialog from '../ModalDialog';

import { useAddPrintTemplateMutation, useDeletePrintTemplateMutation,
  useUpdatePrintTemplateMutation } from '../../apis/backend';
import { useGetPrintTemplatesByIdQuery } from '../../apis/backend';

import useStyles from './printStyles';
import { faker } from '@faker-js/faker';


const fontFamilies = [
  { name: 'Courier', value: 'Courier' },
  { name: 'Courier Bold', value: 'CourierBold' },
  { name: 'Courier Oblique', value: 'CourierOblique ' },
  { name: 'Courier BoldOblique', value: 'CourierBoldOblique' },
  { name: 'Helvetica', value: 'Helvetica' },
  { name: 'Helvetica Bold', value: 'HelveticaBold' },
  { name: 'Helvetica Oblique', value: 'HelveticaOblique' },
  { name: 'Helvetica BoldOblique', value: 'HelveticaBoldOblique' },
  { name: 'Times New Roman', value: 'TimesRoman' },
  { name: 'Times Bold', value: 'TimesRomanBold' },
  { name: 'Times Italic', value: 'TimesRomanItalic' },
  { name: 'Times BoldItalic', value: 'TimesRomanBoldItalic' }
];

const width = 2480;
const height = 3508;
const defaultText = {
  name: '',
  text: '',
  width: 0,
  fontFamily: '',
  size: '',
};

const EDITOR_WRAPPER_ID = 'editorWrapper';
const EDITOR_ID = 'editor';

const PrintEditor = () => {
  const [tool, setTool] = useState();
  const [text, setText] = useState(defaultText);
  const [elements, setElements] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState();
  const [color, setColor] = useState();
  const [pdf, setPdf] = useState();

  const colorRef = useRef('#000000');

  let draggedElement, afterElement;
  let container;
  let originX, originY;
  let lastX, lastY;
  let direction;
  let selectedElement;
  let selectedElementWrapper;
  let updateColor;

  const [anchorEl, setAnchorEl] = useState();
  const [imageBrowserDialogOpen, setImageBrowserDialogOpen] = useState();
  const [saveDialogOpen, setSaveDialogOpen] = useState();
  const [imageAction, setImageAction] = useState();
  const [showTextForm, setShowTextForm] = useState();
  const [images, setImages] = useState({});
  const [imagesToImport, setImagesToImport] = useState([]);

  const history = useHistory();
  const { id: printTemplateId } = useParams();

  const chainId = useSelector(state => state.doris.selectedChain);
  const siteId = useSelector(state => state.doris.selectedSite);

  const [skipGetPrintTemplate, setSkipGetPrintTemplate] = useState(true);
  const {data: printTemplate, isLoading: isLoadingPrintTemplate } = useGetPrintTemplatesByIdQuery(printTemplateId, { skip: skipGetPrintTemplate });

  const [addPrint, { status: addStatus, error: addError, data: addData }] = useAddPrintTemplateMutation();
  const [updatePrint, { status: updateStatus, error: updateError, data: updateData }] = useUpdatePrintTemplateMutation();
  const [deletePrint, { status: deleteStatus, error: deleteError, data: deleteData }] = useDeletePrintTemplateMutation();
  const [currentPrintTemplate, setPrintTemplate] = useState();


  const classes = useStyles();

  const getElementMetadata = (elementName) => {
    return elements.find(el => el.name == elementName);
  };

  const updateElementMetadata = (data, elementName) => {
    const element = getElementMetadata(elementName);
    const filteredElements = elements.filter(el => el.name != elementName);
    setElements([].concat(...filteredElements, {...element, ...data})
      .sort((a, b) => b.zIndex - a.zIndex));
  };

  const addElementMetadata = (newElements) => {
    setElements([].concat(...elements, ...newElements)
      .sort((a, b) => b.zIndex - a.zIndex));
  };

  const sortDraggableElements = (newList) => {
    let numberOfItems = newList.length;
    for (let i = 0; i < numberOfItems; i++) {
      const element = document.querySelector(`#${newList[i].getAttribute('data-id')}`);
      element.parentNode.style.zIndex = numberOfItems - i;
    }
  };

  const handleEditorMouseMove = (event) => {
    if (!selectedElementWrapper) return;

    const editor = document.getElementById('editor');
    const moveRatio = width / editor.offsetWidth;

    switch (tool) {
    case 'move':
      handleElementMove(event, moveRatio);
      break;

    case 'resize':
      const elementType = getElementMetadata(selectedElement.id).type;
      if (elementType == 'text') {
        mouseMoveResize(event, moveRatio);
      } else {
        event.preventDefault();
      }
      break;
    }
  };

  const handleEditorMouseUp = (event) => {
    if (!selectedElementWrapper) return;

    switch (tool) {
    case 'move':
      handleElementMoveEnd();
      break;
    case 'resize':
      handleElementResizeEnd();
      break;
    case 'text':
      break;
    }
  };

  const handleElementMove = (event, moveRatio) => {
    const limitBottom = selectedElementWrapper.offsetTop >= height - 50;
    const limitTop = selectedElementWrapper.offsetTop <= -selectedElementWrapper.offsetHeight + 50;
    const limitLeft = selectedElementWrapper.offsetLeft <= -selectedElementWrapper.offsetWidth + 50;
    const limitRight = selectedElementWrapper.offsetLeft >= width - 50;
    if (limitBottom) {
      selectedElementWrapper.style.top = height - 55 + 'px';
      return;
    } else if (limitTop) {
      selectedElementWrapper.style.top = -selectedElementWrapper.offsetHeight + 55 + 'px';
      return;
    } else if (limitLeft) {
      selectedElementWrapper.style.left = -selectedElementWrapper.offsetWidth + 55 + 'px';
      return;
    } else if (limitRight) {
      selectedElementWrapper.style.left = width - 55 + 'px';
      return;
    }

    const left = selectedElementWrapper.offsetLeft + (event.pageX-lastX)*moveRatio;
    const top = selectedElementWrapper.offsetTop + (event.pageY-lastY)*moveRatio;
    selectedElementWrapper.style.left = left + 'px';
    selectedElementWrapper.style.top = top + 'px';

    lastX = event.pageX;
    lastY = event.pageY;

    event.preventDefault();
  };

  const mouseMoveResize = (event, moveRatio) => {
    if (direction == 'right') {
      const elementWidth = selectedElement.offsetWidth;
      const displacement = (event.pageX - lastX) * moveRatio;
      selectedElement.style.width = elementWidth +  displacement  + 'px';
    } else if (direction == 'left') {
      const left = selectedElementWrapper.offsetLeft;
      const elementWidth = selectedElement.offsetWidth;
      const displacement = (lastX - event.pageX) * moveRatio;
      selectedElementWrapper.style.left = left - displacement + 'px';
      selectedElement.style.width = elementWidth + displacement + 'px';
    }
    lastX = event.pageX;
  };

  const updateMetaElementCoordinates = (selectedElementWrapper, selectedElement) => {
    const metaElement = elements.find(el => el.name == selectedElement.id);
    const x = parseFloat(selectedElementWrapper.style.left);
    const y = parseFloat(selectedElementWrapper.style.top);
    const oldMetaElements = elements.filter(i => i.name != selectedElement.id);
    const updatedMetaElements = [ ...oldMetaElements, { ...metaElement, coordinates: { x, y} }];
    setElements(updatedMetaElements.sort((a, b) => b.zIndex - a.zIndex));
  };

  const generatePDF = async () => {
    const pdf = await createPdf(width, height, elements.filter(el => el.hidden == false), images);
    setPdf(pdf);
  };

  const handleLayerSelect = (elementName) => {
    setSelectedLayer(elementName);
    const currentElementMetadata = getElementMetadata(elementName);
    if (!currentElementMetadata) return;
    switch (currentElementMetadata.type) {
    case 'text':
      setText({
        name: currentElementMetadata.name,
        text: currentElementMetadata.text,
        width: currentElementMetadata.width,
        fontFamily: currentElementMetadata.font,
        size: currentElementMetadata.size,
      });
      setColor(currentElementMetadata.color);
      colorRef.current = currentElementMetadata.color;
      break;
    }
  };

  const handleTextLayerMutation = () => {
    const element = getElementMetadata(selectedLayer);
    const newData = {
      name: element.name,
      text: text.text,
      width: text.width,
      size: parseInt(text.size),
      font: text.fontFamily
        ? text.fontFamily
        : element.font,
      color
    };

    updateElementMetadata(newData, selectedLayer);
  };

  const handleTextLayerSubmit = () => {
    selectedLayer ? handleTextLayerMutation() : handleAddText();
  };

  const handleColorChangeComplete = (color, event) => {
    colorRef.current = color.hex;
    setColor(color.hex);
  };

  const handleAddText = () => {
    if (!text.text) return;

    const textName = faker.lorem.slug();
    const newText = {
      type: 'text',
      name: textName,
      text: text.text,
      coordinates: { x: width / 2, y: height / 2 },
      width: parseFloat(text.width) || width / 3,
      height: 480,
      scale: null,
      rotate: null,
      hidden: false,
      size: parseInt(text.size),
      font: text.fontFamily,
      lineHeight: parseInt(text.size),
      zIndex: elements.length + 1,
      color: colorRef.current ? colorRef.current : '#000000'
    };

    setElements([].concat(...elements, newText).sort((a, b) => b.zIndex - a.zIndex));
    setSelectedLayer(textName);
  };

  const handleDragStart = (event) => {
    draggedElement = event.target;
    if (!draggedElement.classList.contains('draggable')) {
      event.preventDefault();
    } else {
      event.dataTransfer.setData('text/plain',null);
      container = document.querySelector('.dropzone');
      draggedElement.classList.add('dragging');
    }
  };

  const getDragAfterElement = (container, y) => {
    const elements = [...document.querySelectorAll('.draggable:not(.dragging)')];
    return elements.reduce((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;
      if (offset < 0 && offset > closest.offset) {
        return { offset, element: child };
      } else {
        return closest;
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element;
  };

  const handleDragOver = (event) => {
    afterElement = getDragAfterElement(container, event.clientY);
    if (afterElement == null) {
      container.appendChild(draggedElement);
    } else {
      container.insertBefore(draggedElement, afterElement);
    }
  };

  const handleDragEnd = (event) => {
    event.target.classList.remove('dragging');
    const draggableElements = document.querySelectorAll('.draggable');
    sortDraggableElements(draggableElements);
  };

  const handleElementMoveStart = (event) => {
    const toolIsMove = tool == 'move';
    const elementLayerIsActive = event.target.id == selectedLayer;
    if (toolIsMove) {
      event.preventDefault();
    }
    selectedElement = selectedLayer
      ? document.getElementById(selectedLayer)
      : undefined;
    selectedElementWrapper = selectedElement
      ? selectedElement.parentNode
      : undefined;
    originX = event.pageX;
    originY = event.pageY;
    lastX = event.pageX;
    lastY = event.pageY;
  };

  const handleElementMoveEnd = () => {
    if (originX == lastX && originY == lastY) return;
    updateMetaElementCoordinates(selectedElementWrapper, selectedElement);
    selectedElement = null;
    selectedElementWrapper = null;
    lastX = lastY = null;
    originX = originY = null;
  };

  const handleTextResizeStart = (event) => {
    selectedElement = selectedLayer
      ? document.getElementById(selectedLayer)
      : undefined;
    selectedElementWrapper = selectedElement
      ? selectedElement.parentNode
      : undefined;
    lastX = event.pageX;
    lastY = event.pageY;
    originX = event.pageX;
    originY = event.pageY;

    const classes = event.target.classList;
    if (classes.contains('rightExpand')) {
      direction = 'right';
    } else if (classes.contains('leftExpand')) {
      direction = 'left';
    }
  };


  const handleElementResizeEnd = () => {
    const metaElement = elements.find(element => element.name == selectedElement.id);
    setElements([].concat(
      ...elements.filter(el => el.name !== selectedElement.id),
      direction == 'right'
        ? { ...metaElement, width: selectedElementWrapper.offsetWidth }
        : {
          ...metaElement,
          width: selectedElementWrapper.offsetWidth,
          coordinates: {
            ...metaElement.coordinates,
            x: selectedElementWrapper.offsetLeft
          }
        }).sort((a, b) => b.zIndex - a.zIndex));

    setText({...text, width: selectedElementWrapper.offsetWidth });

    selectedElement = null;
    selectedElementWrapper = null;
    direction = null;
    lastX = lastY = null;
    originX = originY = null;
  };

  const handleItemVisibility = (event) => {
    const id = event.target.closest('.draggable').getAttribute('data-id');
    const element = elements.find(i => i.name == id);
    const updatedElements = [...elements.filter(el => el.name != id), { ...element, hidden: !element.hidden }];
    updatedElements.sort((a, b) => b.zIndex - a.zIndex);
    setElements(updatedElements);
  };

  const handleDeleteLayer = (event) => {
    const li = event.target.closest('li');
    const id = li.getAttribute('data-id');
    setElements(elements.filter(i => i.name != id));
    if (selectedLayer) {
      const selectedLayerWasDeleted = !elements
        .find(el => el.name == selectedLayer)?.lenght;
      setShowTextForm(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (action, event) => {
    setAnchorEl(null);
    if (action) setImageAction(action);
  };

  const handleEditorClick = (event) => {
    const rect = event.target.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
  };

  const buildNewImageElementMetadata = (image, index) => {
    return {
      type: 'image',
      name: faker.lorem.slug(),
      file_name: image.file_name,
      url: image.url,
      coordinates: { x: width/2, y: height/2 },
      hidden: false,
      zIndex: elements.length + index,
    };
  };

  const handleImportImages = async (event) => {
    const images = imagesToImport.map((item, index) => {
      const url = `https://yklmtst.blob.core.windows.net/ykl-mt-files/${item.file_name}`;
      const image = { url, file_name: item.file_name };
      return buildNewImageElementMetadata(image, index + elements.length);
    });

    addElementMetadata(images);
    setImageBrowserDialogOpen(false);
    setImagesToImport([]);
  };

  const getImageElements = (elements) => elements.filter(element => element.type == 'image');

  const fetchImage = async (imageElementMetadata) => {
    try {
      const response = await fetch(imageElementMetadata.url);
      const blob = await response.blob();
      const image = await blobToBase64(blob);
      const mime_type = response.headers.get('content-type');
      return {
        name: imageElementMetadata.name,
        image: image.slice(0, image.size, mime_type),
        mime_type
      };
    } catch (error) {
      console.log(error);
    }
  };

  function blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  const downloadImages = async () => {
    const newDownloadedImages = {};
    const currentImageElements = getImageElements(elements);
    const downloadedImages = Object.keys(images);
    for (let i = 0; i < currentImageElements.length; i++) {
      const name = currentImageElements[i].name;
      const imageHasBeenDownloaded = downloadedImages.includes(name);
      if (!imageHasBeenDownloaded) {
        const imageData = await fetchImage(currentImageElements[i]);
        newDownloadedImages[name] = imageData;
      }
    }
    setImages({ ...images, ...newDownloadedImages});
  };

  let resizeTimeout = null;
  window.addEventListener('resize', (e) => {
    const editorSelector = `#${EDITOR_WRAPPER_ID} #${EDITOR_ID}`;
    const eventIsInContext = document.querySelector(editorSelector);
    if (!eventIsInContext) return;
    if (resizeTimeout !== null) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(calculateDimensions, 200);
  });

  let source;
  const calculateDimensions = () => {
    const canvas = document.getElementById('canvas');
    const canvasPadding = parseFloat(window.getComputedStyle(canvas, null).getPropertyValue('padding'));
    const editor = document.getElementById('editor');
    const editorWidth = canvas.offsetWidth - canvasPadding*2;
    editor.style.width = editorWidth + 'px';
    editor.style.height = editorWidth * (3508/2480) + 'px';
    editor.style.transform = `scale(${editorWidth/2480})`;
    editor.style.transformOrigin = '0 0';
  };

  const editor = (
    <Box id="editorWrapper"
      onMouseMove={handleEditorMouseMove}
      onMouseUp={handleEditorMouseUp}>
      <Box id="editor"
        onClick={handleEditorClick}>
        {elements.map((element, index, arr) => {
          const isSelected = element.name == selectedLayer;
          switch (element.type) {
          case 'image':
            return (
              <ImageElement
                key={index}
                arr={arr}
                index={index}
                element={element}
                imageData={images[element.name]}
                isSelected={isSelected}
                handleElementMoveStart={handleElementMoveStart}
              />
            );
          case 'text':
            return (
              <TextElement
                arr={arr}
                text={text}
                tool={tool}
                index={index}
                key={index}
                color={color}
                element={element}
                isSelected={isSelected}
                handleElementMoveStart={handleElementMoveStart}
                handleTextResizeStart={handleTextResizeStart}
              />
            );
          }
        })}
      </Box>
    </Box>
  );

  const imageBrowserDialogContent = (
    <PrintPhotoBrowser
      imagesToImport={imagesToImport}
      setImagesToImport={setImagesToImport} />
  );

  const imageBrowserDialogActions = (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleImportImages}>
        Import
      </Button>
      <Button onClick={() => setImageBrowserDialogOpen(false)} color="primary">
        close
      </Button>
    </>
  );

  const handlePrintDelete = async (event) => {
    try {
      await deletePrint(printTemplateId).unwrap();
      toast.success(`Print "${printTemplateId}" was successfully deleted.`);
      history.push('/templates/print');
    } catch (error) {
      console.error(error);
      toast.error(`Unable to delete print "${printTemplateId}".`);
    }
  };
  const handlePrintSubmit = async (e, currentPrintTemplate)  => {
    try {
      const payload = {
        ongoing: false,
        ...currentPrintTemplate,
        chain_id: chainId,
        site_id: siteId,
        elements
      };

      if (printTemplateId) {
        await updatePrint({ payload, chainId, siteId }).unwrap();
        toast.success(`Print template "${printTemplateId}" was updated successfully`);
      } else {
        const printTemplateId = await addPrint({ payload, chainId, siteId }).unwrap();
        toast.success(`Print template "${printTemplateId}" was saved successfully`);
      }

      history.push('/templates/print');
    } catch (error) {
      const message = printTemplateId
        ? `[${error.status}] - Unable to update ${printTemplateId}`
        : `[${error.status}] - Unable to save print template`;
      toast.error(message);
      console.error(error);
    }
  };

  const printSaveFromDialogContent = (
    <PrintSaveForm
      elements={elements}
      printTemplate={printTemplate}
      currentPrintTemplate={currentPrintTemplate}
      setPrintTemplate={setPrintTemplate} />
  );

  const disabledPrintSaveDialogActions = (
    addStatus == 'pending'
    || updateStatus == 'pending'
    || deleteStatus == 'pending'
  );

  const printSaveDialogActions = (
    <>
      <Button
        disabled={disabledPrintSaveDialogActions}
        variant="contained"
        color="primary"
        onClick={(e) => handlePrintSubmit(e, currentPrintTemplate)}>
        {printTemplateId ? 'Update' : 'Save'}
      </Button>
      {printTemplateId && <Button
        variant="outlined"
        color="primary"
        disabled={disabledPrintSaveDialogActions}
        onClick={handlePrintDelete}>
          Delete
      </Button>}
      <Button
        variant="outlined"
        color="primary"
        disabled={disabledPrintSaveDialogActions}
        onClick={() => {
          setSaveDialogOpen(false);
          setTool();
        }}>
        Cancel
      </Button>
    </>
  );

  useEffect(() => {
    if (chainId && siteId && printTemplateId) {
      setSkipGetPrintTemplate(false);
    }
  }, [chainId, siteId]);

  useEffect(() => {
    if (printTemplate?.elements) {
      const { elements } = printTemplate;
      setElements([...elements].sort((a, b) => b.zIndex - a.zIndex));
    }
  }, [printTemplate]);

  useEffect(() => {
    if (elements.length) downloadImages();
  }, [elements]);

  useEffect(() => {
    const imageElements = getImageElements(elements);
    generatePDF();
  }, [elements, images]);

  useEffect(() => {
    const currentElement = getElementMetadata(selectedLayer);
    if (currentElement?.type == 'text') {
      setShowTextForm(true);
    } else if (currentElement?.type == 'image') {
      setShowTextForm(false);
    }
  }, [selectedLayer]);

  useEffect(() => {
    calculateDimensions();
  });

  useEffect(() => {
    if (tool == 'save') setSaveDialogOpen(true);
  }, [tool]);

  return (
    <Box className={classes.root} p={3}>
      <Grid container spacing={4}>

        <Grid item xs={12}>
          <Typography variant="h4">Print Editor</Typography>
          <Divider />
        </Grid>

        <Grid id="canvas" item xs={4}>{editor}</Grid>

        <Grid item xs={4}>
          <Paper>
            <ToolMenu
              tool={tool}
              setTool={setTool}
              anchorEl={anchorEl}
              pdf={pdf}
              handleClose={handleClose}
              handleClick={handleClick}
            />
            <Divider />
            <Layers
              items={elements}
              images={images}
              defaultText={defaultText}
              setText={setText}
              setSelectedLayer={setSelectedLayer}
              selectedLayer={selectedLayer}
              setImageAction={setImageAction}
              setOpen={setImageBrowserDialogOpen}
              setShowTextForm={setShowTextForm}
              handleLayerSelect={handleLayerSelect}
              handleItemVisibility={handleItemVisibility}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDragEnd={handleDragEnd}
              handleDeleteLayer={handleDeleteLayer}
            />
            { showTextForm &&
            <TextForm
              textData={text}
              setText={setText}
              fontFamilies={fontFamilies}
              colorRef={colorRef}
              selectedLayer={selectedLayer}
              handleTextLayerSubmit={handleTextLayerSubmit}
              handleColorChangeComplete={handleColorChangeComplete}
            />}
          </Paper>
        </Grid>

        <Grid item xs={4}>
          { pdf
            ? (
              <object
                type="application/pdf"
                height="100%"
                width="100%"
                data={pdf ? pdf : ''}>
              </object>
            )
            : ''
          }
        </Grid>

      </Grid>
      <Dialog
        setTool={setTool}
        open={imageBrowserDialogOpen}
        setOpen={setImageBrowserDialogOpen}
        content={imageBrowserDialogContent}
        actions={imageBrowserDialogActions} />
      <Dialog
        setTool={setTool}
        open={saveDialogOpen}
        setOpen={setSaveDialogOpen}
        content={printSaveFromDialogContent}
        actions={printSaveDialogActions} />
    </Box>
  );
};


export default PrintEditor;